// FilesPanel — the app owner's window onto the SOURCE their app is built from.
// Read-only: a folder tree on the left, the selected file's text on the right.
//
// Why read-only. What is on screen is what the deployers are serving right now,
// which is the thing worth being able to check. Editing here would need a push
// and a redeploy, and the deployer refuses platform-owned paths — so an editable
// pane would be a second, weaker deploy path with its own rules. The operator
// changes the app by asking for a change; this is for seeing what it did.
//
// Warm-paper styling, matching DataPanel / SecretsPanel: same header, same rail,
// same button vocabulary. The tree is grouped by lib/file_tree (tested there);
// this file only renders.

import { useMemo, useState } from "react";
import { useAppFiles } from "../hooks/useAppFiles";
import { allDirPaths, formatBytes, type TreeNode } from "../lib/file_tree";

export default function FilesPanel({ slug }: { slug: string }) {
  const f = useAppFiles(slug);
  // Collapsed folders, by prefix. Expanded is the default: an operator opening
  // this pane wants to SEE the app, and a tree of eight closed folders answers
  // nothing. Collapsing is one click when a folder is in the way.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  const allDirs = useMemo(() => (f.tree ? allDirPaths(f.tree) : []), [f.tree]);

  return (
    <div className="flex h-full w-full flex-col bg-bg">
      {/* NO BAND ACROSS THE TOP.
          It carried a heading that repeated the tab's own name ("Files"), a caption, and three
          controls — a full band of height, the width of the whole panel, to say what the tab already
          said. The controls belong WITH the files they act on, so they now live at the top of the tree
          rail (below), and the viewer gets that height back. */}

      {/* A listing merged from two deployers: say when half of it is missing,
          because a partial tree is indistinguishable from a small app. */}
      {f.partial.length > 0 && (
        <div className="border-b border-warn-border bg-warn-soft px-5 py-2 text-[11px] text-warn">
          Part of the listing could not be read — this tree may be incomplete: {f.partial.join("; ")}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* Tree rail */}
        <nav className="flex w-72 shrink-0 flex-col border-r border-border" aria-label="App files">
          {/* The rail's own toolbar: the count, the fact that this is read-only, and the three controls
              that act on this tree. Sticky-free because the rail scrolls below it, not with it — the
              collapse toggle must stay reachable in a long tree, which is exactly when it is wanted.

              The collapse toggle is an ICON here, where "Collapse all" would not fit beside the count in
              a 288px rail. Its title says the words, and the chevrons flip with the state so the button
              shows what it will DO rather than what it did. */}
          <div className="flex items-center gap-0.5 border-b border-border px-2 py-1.5">
            <p className="min-w-0 flex-1 truncate text-[11px] text-text-tertiary">
              {f.files ? `${f.files.length} file${f.files.length === 1 ? "" : "s"} · read-only` : "read-only"}
            </p>
            {f.tree && allDirs.length > 0 && (
              <button
                type="button"
                onClick={() => setCollapsed((prev) => (prev.size > 0 ? new Set() : new Set(allDirs)))}
                aria-label={collapsed.size > 0 ? "Expand all" : "Collapse all"}
                title={collapsed.size > 0 ? "Expand all" : "Collapse all"}
                className={ICON_BTN}
              >
                {collapsed.size > 0 ? <ChevronsDown /> : <ChevronsUp />}
              </button>
            )}
            <button type="button" onClick={f.reload} aria-label="Refresh" title="Refresh" className={ICON_BTN}><RefreshIcon /></button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto py-2">
          {f.error
            ? <RailNote>Couldn’t load the file list: {f.error}</RailNote>
            : f.tree === null
              ? <RailNote>Loading files…</RailNote>
              : f.tree.length === 0
                ? <RailNote>No files yet — this app hasn’t deployed anything.</RailNote>
                : <ul className="px-1">
                    {f.tree.map((n) => (
                      <Node key={n.path} node={n} depth={0} openPath={f.open?.path ?? null}
                            collapsed={collapsed} onToggle={toggle} onOpen={f.openFile} />
                    ))}
                  </ul>}
          </div>
        </nav>

        {/* Viewer */}
        <section className="flex min-w-0 flex-1 flex-col">
          {f.open === null
            ? <Placeholder />
            : <FileView open={f.open} onClose={f.close} />}
        </section>
      </div>
    </div>
  );
}

function Node({ node, depth, openPath, collapsed, onToggle, onOpen }: {
  node: TreeNode; depth: number; openPath: string | null;
  collapsed: Set<string>; onToggle: (p: string) => void; onOpen: (p: string) => void;
}) {
  const pad = { paddingLeft: `${depth * 12 + 8}px` };

  if (node.kind === "dir") {
    const isCollapsed = collapsed.has(node.path);
    return (
      <li>
        <button
          type="button"
          onClick={() => onToggle(node.path)}
          aria-expanded={!isCollapsed}
          style={pad}
          className="flex w-full items-center gap-1.5 rounded-md py-1 pr-2 text-left text-xs font-medium text-text-secondary transition hover:bg-surface-muted hover:text-text"
        >
          <Chevron open={!isCollapsed} />
          <span className="truncate">{node.name}</span>
        </button>
        {!isCollapsed && (
          <ul>
            {node.children.map((c) => (
              <Node key={c.path} node={c} depth={depth + 1} openPath={openPath}
                    collapsed={collapsed} onToggle={onToggle} onOpen={onOpen} />
            ))}
          </ul>
        )}
      </li>
    );
  }

  const active = openPath === node.path;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(node.path)}
        aria-current={active ? "true" : undefined}
        style={pad}
        title={node.path}
        className={
          "flex w-full items-center justify-between gap-2 rounded-md py-1 pr-2 text-left text-xs transition " +
          (active ? "bg-accent-soft font-medium text-accent" : "text-text-secondary hover:bg-surface-muted hover:text-text")
        }
      >
        <span className="truncate font-mono">{node.name}</span>
        <span className="shrink-0 text-[10px] text-text-tertiary">{formatBytes(node.bytes)}</span>
      </button>
    </li>
  );
}

// Line numbers are not decoration here: every diagnostic this platform produces
// names a file:line, so a viewer without them cannot answer the question the
// operator arrived with.
const MAX_RENDERED_LINES = 5000;

function FileView({ open, onClose }: { open: NonNullable<ReturnType<typeof useAppFiles>["open"]>; onClose: () => void }) {
  const lines = useMemo(
    () => (open.content === null ? [] : open.content.split("\n")),
    [open.content],
  );
  const shown   = lines.slice(0, MAX_RENDERED_LINES);
  const clipped = lines.length - shown.length;

  return (
    <>
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2">
        <code className="min-w-0 truncate font-mono text-xs text-text" title={open.path}>{open.path}</code>
        <button type="button" onClick={onClose} className={BTN_GHOST}>Close</button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {open.binary
          ? <ViewerNote>This is a binary file — there is nothing to show as text.</ViewerNote>
          : open.error
            ? <ViewerNote>Couldn’t read this file: {open.error}</ViewerNote>
            : open.content === null
              ? <ViewerNote>Loading…</ViewerNote>
              : open.content === ""
                ? <ViewerNote>This file is empty.</ViewerNote>
                : <div className="min-w-0 overflow-x-auto">
                  {/* UN TABLEAU DÉFILE DANS SA BOÎTE, il ne pousse pas la page.
          `w-full` ne borne rien : la largeur INTRINSÈQUE d'un tableau (celle que son
          contenu exige) l'emporte, donc il déborde de son parent et c'est la PAGE qui
          se met à défiler de côté — la panne responsive classique, et la pire, parce
          qu'elle casse tout l'écran et pas seulement le tableau.
          `min-w-0` sur la boîte : sans lui, un enfant de flex refuse de rétrécir sous
          sa largeur intrinsèque et le débordement remonte d'un cran. */}
                  <table className="w-full border-collapse font-mono text-[11px] leading-[1.55]">
                    <tbody>
                      {shown.map((line, i) => (
                        <tr key={i} className="align-top">
                          <td className="w-12 select-none border-r border-border/60 px-2 text-right text-text-tertiary">{i + 1}</td>
                          <td className="whitespace-pre px-3 text-text">{line || " "}</td>
                        </tr>
                      ))}
                      {clipped > 0 && (
                        <tr>
                          <td className="w-12 border-r border-border/60" />
                          <td className="px-3 py-2 font-sans text-[11px] text-text-tertiary">
                            … {clipped.toLocaleString()} more line{clipped === 1 ? "" : "s"} not shown
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>}
      </div>
    </>
  );
}

function Placeholder() {
  return (
    <div className="flex h-full items-center justify-center px-4 sm:px-8">
      <div className="max-w-sm text-center">
        <p className="text-sm font-medium text-text">Pick a file</p>
        <p className="mt-1.5 text-xs leading-relaxed text-text-secondary">
          This is the app’s own source, exactly as it is deployed — the backend’s{" "}
          <code className="font-mono">api/</code> and <code className="font-mono">contract/</code>, the UI under{" "}
          <code className="font-mono">frontend/</code>, and the tests. Read-only.
        </p>
      </div>
    </div>
  );
}

function RailNote({ children }: { children: React.ReactNode }) {
  return <p className="px-4 py-6 text-xs leading-relaxed text-text-secondary">{children}</p>;
}
function ViewerNote({ children }: { children: React.ReactNode }) {
  return <p className="px-5 py-8 text-center text-xs text-text-secondary">{children}</p>;
}

const ICON_BTN  = "rounded-md p-1.5 text-text-secondary transition hover:bg-surface-muted hover:text-text";
const BTN_GHOST = "rounded-full border border-border px-3 py-1 text-xs text-text-secondary transition hover:border-border-active hover:text-text disabled:opacity-50";

const SVG = (p: { children: React.ReactNode }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{p.children}</svg>
);
function RefreshIcon() { return <SVG><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></SVG>; }
// Collapse / expand, as a pair of chevrons pointing the way the tree will move. Shown by what the
// button DOES, not by what state it is in: a control labelled with the current state is the classic way
// to make someone click it twice.
function ChevronsUp()   { return <SVG><polyline points="17 11 12 6 7 11" /><polyline points="17 18 12 13 7 18" /></SVG>; }
function ChevronsDown() { return <SVG><polyline points="7 13 12 18 17 13" /><polyline points="7 6 12 11 17 6" /></SVG>; }
function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         className={"shrink-0 transition-transform " + (open ? "rotate-90" : "")}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
